import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo, useMotionValueEvent } from 'framer-motion';
import { useLanguage } from './Header';
import { getTranslation, Language } from '../translations';
import { TermsContent, PrivacyContent } from './LegalContent';
import { ContactModalOverlay, ContactModalDialog, ContactModalBrandPane, ContactModalFormPane, CloseIconButton, ContactPreferenceLabel, Checkmark } from './cart/components/CartContactModal';
import { Star, ShieldCheck } from 'lucide-react';

const FooterWrapper = styled.footer`
  margin-top: 0;
  padding-top: clamp(2rem, 1.5rem + 2vw, 3rem);
  padding-bottom: clamp(1.4rem, 1.2rem + 1.4vw, 2.4rem);
  background: #f8fafc;
  border-top: 1px solid rgba(17, 24, 39, 0.08);
  color: #111827;

  .dark & {
    background: linear-gradient(to bottom, #0a0c10 0%, #121418 100%);
    border-top: 1px solid rgba(148, 163, 184, 0.2);
    color: #e2e8f0;
  }

  @media (max-width: 768px) {
    padding-top: 2rem;
    padding-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding-top: 1.25rem;
    padding-bottom: 1rem;
  }
`;

const FooterInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 clamp(1.2rem, 0.9rem + 1.7vw, 2rem);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 0 1.5rem;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 0 1.5rem;
    gap: 1rem;
  }
`;

const HeroSection = styled.div`
  display: grid;
  gap: clamp(1rem, 0.8rem + 0.6vw, 1.5rem);
  align-items: stretch;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const HeroPanel = styled.div`
  padding: 1.75rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  .dark & {
    background: linear-gradient(135deg, #0f1216 0%, #111827 100%);
    border-color: #1e293b;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 18px;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1.125rem;
    border-radius: 14px;
    gap: 0.625rem;
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  background: #0f172a;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f8fafc;

  svg { width: 12px; height: 12px; }

  .dark & {
    background: #334155;
    color: #e2e8f0;
  }

  @media (max-width: 768px) {
    align-self: center;
  }
`;

const HeroBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ReviewPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 700;
  color: #92400e;

  .stars {
    display: flex;
    gap: 1px;
  }

  .dark & {
    background: linear-gradient(135deg, #422006 0%, #451a03 100%);
    border-color: #92400e;
    color: #fbbf24;
  }
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0.5rem 0;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;

  .dark & {
    background: #1a1f2e;
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    border-radius: 10px;
    padding: 0.375rem 0;
  }
`;

const HeroStat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  padding: 0.5rem 0;

  strong {
    font-size: 1.1rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
  }

  span {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin-top: 0.2rem;
  }

  .dark & strong { color: #f1f5f9; }
  .dark & span { color: #64748b; }

  @media (max-width: 480px) {
    padding: 0.375rem 0;

    strong {
      font-size: 0.95rem;
    }

    span {
      font-size: 0.55rem;
    }
  }
`;

const DividerVertical = styled.div`
  width: 1px;
  height: 28px;
  background: #e2e8f0;

  .dark & { background: #334155; }
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 1.2rem + 0.8vw, 1.75rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  color: #111827;

  .dark & {
    color: #f8fafc;
  }

  @media (max-width: 768px) {
    font-size: 1.35rem;
    line-height: 1.2;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1.15rem;
    line-height: 1.25;
  }
`;

const HeroDescription = styled.p`
  margin: 0;
  max-width: 520px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #6b7280;

  .dark & {
    color: #cbd5e1;
  }

  @media (max-width: 768px) {
    font-size: 0.9375rem;
    text-align: center;
    max-width: 100%;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    font-size: 0.775rem;
    line-height: 1.5;
    margin-bottom: 0.25rem;
  }
`;

const Checklist = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.5rem 0.625rem;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  flex: 1;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.375rem;
  }
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.8125rem;
  line-height: 1.3;
  padding: 0.875rem 1.125rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-sizing: border-box;
  width: 100%;
  flex: 1;

  .dark & {
    color: #e2e8f0;
    background: #1a1f2e;
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.625rem 0.75rem;
    border-radius: 8px;
    gap: 0.5rem;
  }
`;

const BulletIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #0f172a;
  color: #ffffff;
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }

  .dark & {
    background: #f1f5f9;
    color: #0f172a;
  }

  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
    border-radius: 5px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const ContactPanel = styled.div`
  padding: 1.75rem;
  border-radius: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #0f172a;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

  .dark & {
    background: linear-gradient(135deg, #0f1216 0%, #111827 100%);
    color: #e2e8f0;
    border-color: #1e293b;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
    border-radius: 18px;
    gap: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1.125rem;
    border-radius: 14px;
    gap: 0.75rem;
  }
`;

const ContactTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ContactSubtitle = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #6b7280;

  .dark & {
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-align: center;
    max-width: 100%;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.45;
  }
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: 480px) {
    gap: 0.375rem;
  }
`;

const ContactItem = styled.li`
  padding: 0;
`;

const ContactIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #0f172a;
  flex-shrink: 0;
  transition: transform 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    color: #ffffff;
  }

  .dark & {
    background: #334155;

    svg {
      color: #e2e8f0;
    }
  }

  @media (max-width: 480px) {
    width: 34px;
    height: 34px;
    border-radius: 8px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const ContactLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  width: 100%;
  color: #0f172a;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-decoration: none;
  font-size: 0.875rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateX(2px);
  }

  &:hover ${ContactIcon} {
    transform: scale(1.05);
  }

  .dark & {
    color: #f8fafc;
    background: #1a1f2e;
    border-color: #2e3441;
  }

  .dark &:hover {
    background: #1e293b;
    border-color: #475569;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
  }
`;

const ContactText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-sizing: border-box;

  .dark & {
    background: #1a1f2e;
    border-color: #2e3441;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    border-radius: 10px;
  }

  width: 100%;
  color: #0f172a;
  font-weight: 600;
  letter-spacing: -0.01em;
  font-size: 0.875rem;

  .dark & {
    color: #f8fafc;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.5rem;
  }
`;

const ContactButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  background: #0f172a;
  border: none;
  color: #f8fafc;
  font-weight: 700;
  font-size: 0.8125rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.25rem;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.25);

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: #1e293b;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.35);
  }

  &:hover svg {
    transform: scale(1.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
  }

  .dark & {
    background: #f1f5f9;
    color: #0f172a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

    &:hover {
      background: #e2e8f0;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    }
  }

  @media (max-width: 480px) {
    padding: 0.625rem 1rem;
    border-radius: 10px;
    font-size: 0.775rem;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: #e5e7eb;

  .dark & {
    background: rgba(148, 163, 184, 0.15);
  }
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 920px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const SocialSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;

  @media (max-width: 920px) {
    align-items: center;
    text-align: center;
  }
`;

const SocialTitle = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;

  .dark & {
    color: #f8fafc;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const SocialCaption = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #6b7280;

  .dark & {
    color: #9ca3af;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-top: 0.25rem;
`;

const SocialIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: #475569;
  transition: all 0.2s ease;
  overflow: hidden;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    color: #ffffff;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.facebook:hover {
    background: #1877F2;
  }

  &.instagram:hover {
    background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
    border-color: #e6683c;
  }

  &.whatsapp:hover {
    background: #25D366;
  }

  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #94a3b8;

    &:hover {
      color: #ffffff;
    }
  }
`;

const MetaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  align-items: flex-end;
  flex: 0 0 auto;

  @media (max-width: 920px) {
    align-items: center;
    border-top: 1px solid rgba(17, 24, 39, 0.08);
    padding-top: 1.25rem;

    .dark & {
      border-top-color: rgba(148, 163, 184, 0.15);
    }
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const MetaTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  align-items: flex-end;

  @media (max-width: 920px) {
    align-items: center;
    text-align: center;
  }
`;

const LogoBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  @media (max-width: 920px) {
    justify-content: center;
  }
`;

const LogoImage = styled.img`
  height: 24px;
  width: auto;
  object-fit: contain;
`;

const LogoIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const LogoTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #111827;

  .dark & {
    color: #f8fafc;
  }

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }

  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const LogoVersion = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 9999px;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .dark & {
    color: #9ca3af;
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
    border-color: rgba(71, 85, 105, 0.4);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 0.625rem;
    padding: 0.3125rem 0.625rem;
  }

  @media (max-width: 480px) {
    font-size: 0.6875rem;
    padding: 0.375rem 0.625rem;
  }
`;

const Credits = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }

  a {
    color: #374151;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.15s ease;

    &:hover {
      color: #111827;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  .dark & {
    color: #94a3b8;

    a {
      color: #cbd5e1;

      &:hover {
        color: #f1f5f9;
      }
    }
  }
`;

const LegalLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegalButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.8125rem;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
  cursor: pointer;
  transition: color 0.15s ease;
  text-decoration: none;

  svg { display: none; }

  &:hover {
    color: #111827;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .dark & {
    color: #94a3b8;

    &:hover {
      color: #f1f5f9;
    }
  }
`;

const Separator = styled.span`
  color: #d1d5db;
  font-size: 0.75rem;
  user-select: none;

  .dark & {
    color: #475569;
  }
`;


const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  will-change: opacity;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 10000;
  overflow: hidden;
  touch-action: none;
  pointer-events: auto;
  
  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
    overflow-y: hidden;
    -webkit-overflow-scrolling: none;
  }
`;

const ModalCard = styled(motion.div)`
  background: #ffffff;
  border-radius: 20px;
  width: min(900px, 100%);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
  border: 1.5px solid rgba(17, 24, 39, 0.1);
  will-change: transform, opacity;

  .dark & {
    background: #0f172a;
    border-color: rgba(71, 85, 105, 0.4);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-height: 85vh;
    border-radius: 24px 24px 0 0;
    border-bottom: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.75rem 2rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.1);
  background: #ffffff;
  flex-shrink: 0;
  
  .dark & {
    background: #0f172a;
    border-bottom-color: rgba(71, 85, 105, 0.35);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    position: relative;
    padding-bottom: 1.25rem;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 4px;
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`;

const ModalHeading = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #0f172a;
  
  .dark & {
    color: #f1f5f9;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const ModalClose = styled.button`
  background: #f8fafc;
  border: 1px solid rgba(17, 24, 39, 0.1);
  border-radius: 10px;
  color: #64748b;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.375rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: #f1f5f9;
    border-color: rgba(17, 24, 39, 0.15);
    color: #0f172a;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  .dark & {
    background: #1e293b;
    border-color: rgba(71, 85, 105, 0.4);
    color: #94a3b8;
    
    &:hover {
      background: #334155;
      border-color: rgba(71, 85, 105, 0.5);
      color: #e2e8f0;
    }
  }
`;

const ModalBody = styled.div`
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(85vh - 90px);
  flex: 1;
  min-height: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f8fafc;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  .dark & {
    &::-webkit-scrollbar-track {
      background: #1e293b;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #475569;
      
      &:hover {
        background: #64748b;
      }
    }
  }
  
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    overscroll-behavior-y: contain;
    overscroll-behavior-block: contain;
    max-height: calc(85vh - 80px);
  }
`;

type SocialLinkConfig = {
  name: string;
  href: string;
  Icon: () => JSX.Element;
  className?: string;
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.54 2.37a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.71-1.71a2 2 0 0 1 2.11-.45c.77.24 1.56.42 2.37.54A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4.5" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const WhatsappIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.463 3.488" />
  </svg>
);

const LeafIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z" />
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2c2.5 0 4.5 4.5 4.5 10s-2 10-4.5 10-4.5-4.5-4.5-10S9.5 2 12 2z" />
  </svg>
);

const RecycleIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 19l-4-4 4-4" />
    <path d="M13.73 19H16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H8" />
    <polyline points="12 13 14 11 12 9" />
    <path d="M17 5l4 4-4 4" />
    <path d="M10.27 5H8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h8" />
    <polyline points="12 11 10 9 12 7" />
  </svg>
);



const socialLinks: SocialLinkConfig[] = [
  { name: 'Facebook', href: 'https://www.facebook.com/rkphones/', Icon: FacebookIcon, className: 'facebook' },
  { name: 'Instagram', href: 'https://www.instagram.com/phonebros.be/', Icon: InstagramIcon, className: 'instagram' },
  { name: 'WhatsApp', href: 'https://api.whatsapp.com/send?phone=%2B32484899032', Icon: WhatsappIcon, className: 'whatsapp' }
];

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [closingContact, setClosingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '', contactPreference: 'email' });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [showContactSuccess, setShowContactSuccess] = useState(false);

  const handleCloseContact = () => {
    setClosingContact(true);
    setTimeout(() => {
      setShowContact(false);
      setClosingContact(false);
      setShowContactSuccess(false);
      setIsSubmittingContact(false);
      setContactForm({ name: '', email: '', phone: '', message: '', contactPreference: 'email' });
    }, 300);
  };
  const [isMobileLegal, setIsMobileLegal] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  // Refs for scroll containers and headers
  const termsScrollRef = useRef<HTMLDivElement>(null);
  const termsHeaderRef = useRef<HTMLDivElement>(null);
  const privacyScrollRef = useRef<HTMLDivElement>(null);
  const privacyHeaderRef = useRef<HTMLDivElement>(null);

  // Motion values for swipe-to-dismiss
  const termsY = useMotionValue(0);
  const termsOverlayOpacityValue = useMotionValue(1);
  const termsOverlayOpacityTransform = useTransform(termsY, [0, 300], [1, 0]);

  const privacyY = useMotionValue(0);
  const privacyOverlayOpacityValue = useMotionValue(1);
  const privacyOverlayOpacityTransform = useTransform(privacyY, [0, 300], [1, 0]);

  const [termsIsDraggingAllowed, setTermsIsDraggingAllowed] = useState(false);
  const [termsIsScrolling, setTermsIsScrolling] = useState(false);
  const termsDragStartYRef = useRef<number | null>(null);
  const termsScrollStartRef = useRef<number>(0);

  const [privacyIsDraggingAllowed, setPrivacyIsDraggingAllowed] = useState(false);
  const [privacyIsScrolling, setPrivacyIsScrolling] = useState(false);
  const privacyDragStartYRef = useRef<number | null>(null);
  const privacyScrollStartRef = useRef<number>(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLegal(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync overlay opacity with transform
  useMotionValueEvent(termsOverlayOpacityTransform, 'change', (latest) => {
    termsOverlayOpacityValue.set(latest);
  });

  useMotionValueEvent(privacyOverlayOpacityTransform, 'change', (latest) => {
    privacyOverlayOpacityValue.set(latest);
  });

  // Track scrolling for Terms modal
  useEffect(() => {
    const container = termsScrollRef.current;
    if (!container || !isMobileLegal || !showTerms) return;

    let scrollTimer: NodeJS.Timeout | null = null;
    let lastScrollTop = container.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);

      // Only set scrolling if there's actual movement
      if (scrollDelta > 3) {
        setTermsIsScrolling(true);

        // If drag is already allowed, cancel it during active scroll
        // Check via ref to avoid dependency issues
        if (termsIsDraggingAllowed) {
          setTermsIsDraggingAllowed(false);
          termsY.set(0);
        }

        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }

        // Reset scrolling flag faster when scroll stops
        scrollTimer = setTimeout(() => {
          setTermsIsScrolling(false);
        }, 150);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [isMobileLegal, showTerms]);

  // Track scrolling for Privacy modal
  useEffect(() => {
    const container = privacyScrollRef.current;
    if (!container || !isMobileLegal || !showPrivacy) return;

    let scrollTimer: NodeJS.Timeout | null = null;
    let lastScrollTop = container.scrollTop;

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);

      // Only set scrolling if there's actual movement
      if (scrollDelta > 3) {
        setPrivacyIsScrolling(true);

        // If drag is already allowed, cancel it during active scroll
        // Check via ref to avoid dependency issues
        if (privacyIsDraggingAllowed) {
          setPrivacyIsDraggingAllowed(false);
          privacyY.set(0);
        }

        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }

        // Reset scrolling flag faster when scroll stops
        scrollTimer = setTimeout(() => {
          setPrivacyIsScrolling(false);
        }, 150);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [isMobileLegal, showPrivacy]);

  // Reset motion values when modals close
  useEffect(() => {
    if (!showTerms) {
      termsY.set(0);
      termsOverlayOpacityValue.set(1);
      setTermsIsDraggingAllowed(false);
      setTermsIsScrolling(false);
    }
  }, [showTerms]);

  useEffect(() => {
    if (!showPrivacy) {
      privacyY.set(0);
      privacyOverlayOpacityValue.set(1);
      setPrivacyIsDraggingAllowed(false);
      setPrivacyIsScrolling(false);
    }
  }, [showPrivacy]);

  // Lock body scroll when modals are open (wheel event method - matches repair)
  useEffect(() => {
    if (!(showTerms || showPrivacy)) return;
    const onWheel = (e: WheelEvent) => {
      const overlay = (e.target as HTMLElement)?.closest('[data-legal-overlay]');
      if (!overlay) e.preventDefault();
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [showTerms, showPrivacy]);

  const t = useMemo(
    () => (key: string) => getTranslation(language as Language, key),
    [language]
  );

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingContact(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setShowContactSuccess(true);
    setTimeout(() => { handleCloseContact(); }, 3000);
    setIsSubmittingContact(false);
  };

  // Animation variants for legal modals (like HowItWorksModal)
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = useMemo(() => (isMobileLegal
    ? {
        hidden: { y: '100%', opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { type: 'tween' as const, duration: 0.25, ease: [0.25, 0.1, 0.25, 1] as const } }
      }
    : {
        hidden: { y: 12, opacity: 0, scale: 0.98 },
        visible: { y: 0, opacity: 1, scale: 1, transition: { type: 'tween' as const, duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const } }
      }
  ), [isMobileLegal]);

  // Drag handlers for Terms modal
  const handleTermsDragStart = (event: any) => {
    if (!isMobileLegal) {
      setTermsIsDraggingAllowed(false);
      termsY.set(0);
      return false;
    }

    // Save scroll position and lock it without changing overflow
    if (termsScrollRef.current) {
      termsScrollStartRef.current = termsScrollRef.current.scrollTop;
      // Prevent scrolling by disabling touch events on scroll container
      termsScrollRef.current.style.touchAction = 'none';
      // Force disable scroll tracking
      setTermsIsScrolling(false);
    }

    // Allow drag from anywhere on mobile - no scroll check
    setTermsIsDraggingAllowed(true);
    const point = event.point || { y: event.clientY || 0 };
    termsDragStartYRef.current = point.y;
    return true;
  };

  const handleTermsDrag = (_event: any, info: PanInfo) => {
    if (!termsIsDraggingAllowed) {
      termsY.set(0);
      return;
    }

    // Ensure scroll position stays locked during drag
    if (termsScrollRef.current && termsScrollRef.current.scrollTop !== termsScrollStartRef.current) {
      termsScrollRef.current.scrollTop = termsScrollStartRef.current;
    }

    // Only allow downward movement
    if (info.offset.y > 0) {
      termsY.set(info.offset.y);
    } else {
      termsY.set(0);
    }
  };

  const handleTermsDragEnd = (_event: any, info: PanInfo) => {
    // Restore scroll capability
    if (termsScrollRef.current) {
      termsScrollRef.current.style.touchAction = '';
    }

    if (!termsIsDraggingAllowed) {
      termsY.set(0);
      termsOverlayOpacityValue.set(1);
      termsDragStartYRef.current = null;
      return;
    }

    const threshold = 100;
    const velocityThreshold = 500;

    if (info.offset.y > threshold || info.velocity.y > velocityThreshold) {
      // Reset scroll before closing
      if (termsScrollRef.current) {
        termsScrollRef.current.scrollTop = 0;
      }
      setShowTerms(false);
    } else {
      // If drag was cancelled, restore scroll position
      if (termsScrollRef.current) {
        termsScrollRef.current.scrollTop = termsScrollStartRef.current;
      }
      termsY.set(0);
      termsOverlayOpacityValue.set(1);
    }
    setTermsIsDraggingAllowed(false);
    termsDragStartYRef.current = null;
  };

  // Drag handlers for Privacy modal
  const handlePrivacyDragStart = (event: any) => {
    if (!isMobileLegal) {
      setPrivacyIsDraggingAllowed(false);
      privacyY.set(0);
      return false;
    }

    // Save scroll position and lock it without changing overflow
    if (privacyScrollRef.current) {
      privacyScrollStartRef.current = privacyScrollRef.current.scrollTop;
      // Prevent scrolling by disabling touch events on scroll container
      privacyScrollRef.current.style.touchAction = 'none';
      // Force disable scroll tracking
      setPrivacyIsScrolling(false);
    }

    // Allow drag from anywhere on mobile - no scroll check
    setPrivacyIsDraggingAllowed(true);
    const point = event.point || { y: event.clientY || 0 };
    privacyDragStartYRef.current = point.y;
    return true;
  };

  const handlePrivacyDrag = (_event: any, info: PanInfo) => {
    if (!privacyIsDraggingAllowed) {
      privacyY.set(0);
      return;
    }

    // Ensure scroll position stays locked during drag
    if (privacyScrollRef.current && privacyScrollRef.current.scrollTop !== privacyScrollStartRef.current) {
      privacyScrollRef.current.scrollTop = privacyScrollStartRef.current;
    }

    // Only allow downward movement
    if (info.offset.y > 0) {
      privacyY.set(info.offset.y);
    } else {
      privacyY.set(0);
    }
  };

  const handlePrivacyDragEnd = (_event: any, info: PanInfo) => {
    // Restore scroll capability
    if (privacyScrollRef.current) {
      privacyScrollRef.current.style.touchAction = '';
    }

    if (!privacyIsDraggingAllowed) {
      privacyY.set(0);
      privacyOverlayOpacityValue.set(1);
      privacyDragStartYRef.current = null;
      return;
    }

    const threshold = 100;
    const velocityThreshold = 500;

    if (info.offset.y > threshold || info.velocity.y > velocityThreshold) {
      // Reset scroll before closing
      if (privacyScrollRef.current) {
        privacyScrollRef.current.scrollTop = 0;
      }
      setShowPrivacy(false);
    } else {
      // If drag was cancelled, restore scroll position
      if (privacyScrollRef.current) {
        privacyScrollRef.current.scrollTop = privacyScrollStartRef.current;
      }
      privacyY.set(0);
      privacyOverlayOpacityValue.set(1);
    }
    setPrivacyIsDraggingAllowed(false);
    privacyDragStartYRef.current = null;
  };

  // Overlay styles - keep overlay dark during drag (don't change opacity)
  const termsOverlayStyle = undefined;
  const privacyOverlayStyle = undefined;

  const features: string[] = [
    'instantValuation',
    'certifiedDataDeletion',
    'freePickupService',
    'professionalQualityControl'
  ];

  const currentYear = new Date().getFullYear();

  return (
    <>
      <FooterWrapper>
        <FooterInner>
          <HeroSection>
            <HeroPanel>
              <HeroBadgeRow>
                <Tag>
                  <ShieldCheck size={11} strokeWidth={3} />
                  Phone Bros
                </Tag>
                <ReviewPill>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} size={9} fill="#f59e0b" color="#f59e0b" />
                    ))}
                  </div>
                  <span>5/5</span>
                </ReviewPill>
              </HeroBadgeRow>

              <HeroTitle>{t('whyPhoneBros')}</HeroTitle>

              <HeroStats>
                <HeroStat>
                  <strong>80+</strong>
                  <span>{t('reviewsLabel')}</span>
                </HeroStat>
                <DividerVertical />
                <HeroStat>
                  <strong>{t('warrantyMonths')}</strong>
                  <span>{t('warranty')}</span>
                </HeroStat>
                <DividerVertical />
                <HeroStat>
                  <strong>30min</strong>
                  <span>{t('serviceLabel')}</span>
                </HeroStat>
              </HeroStats>

              <HeroDescription>{t('trustedText')}</HeroDescription>

              <Checklist>
                {features.map((item) => (
                  <ChecklistItem key={item}>
                    <BulletIcon>
                      <CheckIcon />
                    </BulletIcon>
                    {t(item)}
                  </ChecklistItem>
                ))}
              </Checklist>
            </HeroPanel>

            <ContactPanel>
              <ContactTitle>{t('contactSupport')}</ContactTitle>
              <ContactSubtitle>{t('humanContactText')}</ContactSubtitle>
              <ContactList>
                <ContactItem>
                  <ContactLink href="mailto:info@phonebros.be">
                    <ContactIcon>
                      <MailIcon />
                    </ContactIcon>
                    <span>info@phonebros.be</span>
                  </ContactLink>
                </ContactItem>
                <ContactItem>
                  <ContactLink href="tel:+32484899032">
                    <ContactIcon>
                      <PhoneIcon />
                    </ContactIcon>
                    <span>+32 484 89 90 32</span>
                  </ContactLink>
                </ContactItem>
                <ContactItem>
                  <ContactText>
                    <ContactIcon>
                      <ClockIcon />
                    </ContactIcon>
                    <span>{t('openingHours')}</span>
                  </ContactText>
                </ContactItem>
              </ContactList>
              <ContactButton type="button" onClick={() => setShowContact(true)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {t('startChat')}
              </ContactButton>
            </ContactPanel>
          </HeroSection>

          <Divider />

          <BottomRow>
            <SocialSection>
              <SocialTitle>{t('followUs')}</SocialTitle>
              <SocialCaption>{t('followUsDescription')}</SocialCaption>
              <SocialLinks>
                {socialLinks.map(({ name, href, Icon, className }) => (
                  <SocialLink key={name} href={href} aria-label={name} className={className}>
                    <Icon />
                  </SocialLink>
                ))}
              </SocialLinks>
            </SocialSection>
            <MetaColumn>
              <LogoBlock>
                <LogoIdentity>
                  <LogoImage src="/images/phonebroslogo.png" alt="Phone Bros Logo" />
                  <LogoTitle>Phone Bros</LogoTitle>
                </LogoIdentity>
                <LogoVersion>v0.50</LogoVersion>
              </LogoBlock>
              <MetaTextContainer>
                <Credits>
                  © {currentYear} Phone Bros — {t('madeBy')}{' '}
                  <a href="https://www.finem.studio" target="_blank" rel="noopener noreferrer">
                    Finem Studio
                  </a>
                </Credits>
                <LegalLinks>
                  <LegalButton onClick={() => setShowTerms(true)}>
                    {t('termsTitle')}
                  </LegalButton>
                  <Separator>·</Separator>
                  <LegalButton onClick={() => setShowPrivacy(true)}>
                    {t('privacyTitle')}
                  </LegalButton>
                </LegalLinks>
              </MetaTextContainer>
            </MetaColumn>
          </BottomRow>
        </FooterInner>
      </FooterWrapper>

      <AnimatePresence>
        {showTerms && (
          <ModalOverlay
            key="terms-overlay"
            data-legal-overlay
            onClick={() => setShowTerms(false)}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2 }}
            style={termsOverlayStyle}
          >
            <ModalCard
              key="terms-modal"
              onClick={(event) => event.stopPropagation()}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              drag={isMobileLegal && !termsIsScrolling ? 'y' : false}
              dragConstraints={isMobileLegal ? { top: 0 } : { top: 0, bottom: 0 }}
              dragElastic={isMobileLegal ? 0.2 : 0}
              dragDirectionLock={isMobileLegal}
              onDragStart={handleTermsDragStart}
              onDrag={handleTermsDrag}
              onDragEnd={handleTermsDragEnd}
              style={{
                y: isMobileLegal ? termsY : undefined,
              }}
              transition={{
                type: 'spring' as const,
                damping: 30,
                stiffness: 300,
              }}
            >
              <ModalHeader ref={termsHeaderRef}>
                <ModalHeading>{t('termsTitle')}</ModalHeading>
                <ModalClose onClick={() => setShowTerms(false)} aria-label={t('close')}>
                  ×
                </ModalClose>
              </ModalHeader>
              <ModalBody ref={termsScrollRef}>
                <TermsContent />
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPrivacy && (
          <ModalOverlay
            key="privacy-overlay"
            data-legal-overlay
            onClick={() => setShowPrivacy(false)}
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2 }}
            style={privacyOverlayStyle}
          >
            <ModalCard
              key="privacy-modal"
              onClick={(event) => event.stopPropagation()}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              drag={isMobileLegal && !privacyIsScrolling ? 'y' : false}
              dragConstraints={isMobileLegal ? { top: 0 } : { top: 0, bottom: 0 }}
              dragElastic={isMobileLegal ? 0.2 : 0}
              dragDirectionLock={isMobileLegal}
              onDragStart={handlePrivacyDragStart}
              onDrag={handlePrivacyDrag}
              onDragEnd={handlePrivacyDragEnd}
              style={{
                y: isMobileLegal ? privacyY : undefined,
              }}
              transition={{
                type: 'spring' as const,
                damping: 30,
                stiffness: 300,
              }}
            >
              <ModalHeader ref={privacyHeaderRef}>
                <ModalHeading>{t('privacyTitle')}</ModalHeading>
                <ModalClose onClick={() => setShowPrivacy(false)} aria-label={t('close')}>
                  ×
                </ModalClose>
              </ModalHeader>
              <ModalBody ref={privacyScrollRef}>
                <PrivacyContent />
              </ModalBody>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Contact Modal - uses same styled components as CartPage */}
      {showContact && createPortal(
        <ContactModalOverlay $closing={closingContact} onClick={handleCloseContact}>
          <ContactModalDialog $closing={closingContact} onClick={e => e.stopPropagation()}>
            <ContactModalBrandPane>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
                  <img src="/images/phonebroslogo.png" alt="Phone Bros" style={{ height: '32px', filter: 'brightness(0) invert(1)' }} />
                  <span style={{ fontSize: '1.25rem', fontWeight: 700 }}>Phone Bros</span>
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.875rem', lineHeight: 1.25 }}>{t('contactFormIntro')}</h2>
                <p style={{ fontSize: '0.9rem', opacity: 0.8, lineHeight: 1.6 }}>{t('humanContactText')}</p>
              </div>
              <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>&copy; {new Date().getFullYear()} Phone Bros &mdash; {t('madeBy')} <a href="https://finemstudio.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', opacity: 0.9 }}>Finem Studio</a></div>
            </ContactModalBrandPane>
            <ContactModalFormPane>
              {showContactSuccess ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center' }}>
                  <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                    <svg style={{ width: '40px', height: '40px' }} fill="white" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  </div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#1e293b', marginBottom: '0.875rem' }}>{t('messageSent')}</h3>
                  <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: '300px' }}>{t('messageConfirmation')}</p>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>{t('contactUs')}</h3>
                    <CloseIconButton onClick={handleCloseContact} aria-label={t('close')}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </CloseIconButton>
                  </div>
                  <p style={{ color: '#475569', marginBottom: '1.25rem', lineHeight: 1.5, fontSize: '0.875rem' }}>{t('contactFormDescription')}</p>
                  <div style={{ background: '#f7f8fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e6edf5', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    <p style={{ color: '#111827', fontSize: '0.8rem', lineHeight: 1.5, margin: 0, fontWeight: 500 }}>{t('languageSupportNoticeL1')}<br />{t('languageSupportNoticeL2')}</p>
                  </div>
                  <form onSubmit={handleContactSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{t('name')} <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="text" required value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} placeholder={t('fullNamePlaceholder')} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' as const, background: '#fff', fontFamily: 'inherit' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{t('email')} <span style={{ color: '#ef4444' }}>*</span></label>
                        <input type="email" required value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} placeholder={t('emailPlaceholder')} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' as const, background: '#fff', fontFamily: 'inherit' }} />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{t('phoneNumber')}</label>
                      <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))} placeholder={t('phonePlaceholder')} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' as const, background: '#fff', fontFamily: 'inherit' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{t('contactPreference')} <span style={{ color: '#ef4444' }}>*</span></label>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <ContactPreferenceLabel>
                          <input type="radio" name="footerContactPref" value="email" checked={contactForm.contactPreference === 'email'} onChange={() => setContactForm(p => ({ ...p, contactPreference: 'email' }))} />
                          <Checkmark><svg viewBox="0 0 16 16" fill="white"><path d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z' /></svg></Checkmark>
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('email')}</span>
                        </ContactPreferenceLabel>
                        <ContactPreferenceLabel>
                          <input type="radio" name="footerContactPref" value="phone" checked={contactForm.contactPreference === 'phone'} onChange={() => setContactForm(p => ({ ...p, contactPreference: 'phone' }))} />
                          <Checkmark><svg viewBox="0 0 16 16" fill="white"><path d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z' /></svg></Checkmark>
                          <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('phone')}</span>
                        </ContactPreferenceLabel>
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: '0.35rem' }}>{t('message')}</label>
                      <textarea rows={3} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))} placeholder={t('messagePlaceholder')} style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' as const, background: '#fff', minHeight: '70px', resize: 'vertical' as const, fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                      <button type="button" onClick={handleCloseContact} style={{ padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', border: '1px solid #cbd5e1', background: 'white', color: '#334155', fontFamily: 'inherit' }}>{t('cancel')}</button>
                      <button type="submit" disabled={isSubmittingContact} style={{ padding: '0.625rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem', cursor: isSubmittingContact ? 'not-allowed' : 'pointer', border: 'none', background: '#374151', color: 'white', fontFamily: 'inherit', opacity: isSubmittingContact ? 0.7 : 1, boxShadow: '0 4px 12px rgba(55,65,81,0.2)' }}>{isSubmittingContact ? '...' : t('sendMessage')}</button>
                    </div>
                  </form>
                </div>
              )}
            </ContactModalFormPane>
          </ContactModalDialog>
        </ContactModalOverlay>,
        document.body
      )}
    </>
  );
};

export default Footer;
